import { ReactNode, useEffect, useState } from "react";
import BuyTicketPopup from "./buyTicket";
import SubscriptionTab from "./subscriptionTab";
import { PrizeTicketProps } from "./types";
import { getPrizeData } from "./prize-ticket-selector-service";
import DOMPurify from "dompurify";
import { getCookie } from "../../../helpers/cookies";
import { getUserSubscriptionPlan } from "../../(general-header)/subscription/subscription-service";
import { AddTicketsData } from "../../common";

type TabKey = "online" | "botbpass" | "postal" | "subscription";

const PrizeTicketSelector = ({
  prizeId,
  onAddTicketCallback,
  defaultTicketCounter,
  addToCartMethodId,
}: {
  prizeId: number;
  onAddTicketCallback?: (data: AddTicketsData) => void;
  defaultTicketCounter?: number;
  addToCartMethodId?: number;
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>("online");
  const [prizeDetails, setPrizeDetails] = useState<PrizeTicketProps>();
  const [shouldShowTabHeaders, setShouldShowTabHeaders] = useState<boolean>(false);
  const [subscriptionTab, setSubscriptionTab] = useState<boolean>(false);
  const [remainingPassTickets, setRemainingPassTickets] = useState<number>(0);
  const isAuth = getCookie("BotB.Authenticated") === "true";

  // Lock body scroll for the whole selector so switching tabs (e.g. to the postal tab,
  // which isn't a BuyTicket) never unlocks the page behind the modal.
  useEffect(() => {
    document.querySelector("body")?.classList.add("!overflow-hidden");
    return () => {
      document.querySelector("body")?.classList.remove("!overflow-hidden");
    };
  }, []);

  useEffect(() => {
    getModalPopupContent();
  }, []);

  useEffect(() => {
    if (isAuth) {
      getUserSubscriptionPlan()
        .then(res => res.json())
        .catch(err => {
          if (prizeDetails?.ShowSubscriptionTab) {
            setSubscriptionTab(true);
            setShouldShowTabHeaders(true);
          } else {
            setSubscriptionTab(false);
          }
        });
    } else {
      if (prizeDetails?.ShowSubscriptionTab) {
        setSubscriptionTab(true);
        setShouldShowTabHeaders(true);
      }
    }
  }, [isAuth, prizeDetails]);

  const getModalPopupContent = async () => {
    const response = await getPrizeData(prizeId?.toString());
    const data = (await response.json()) as PrizeTicketProps;

    if (data?.PostalEntryText || data?.PrizeTicketModel?.SubscriptionTickets > 0) {
      setShouldShowTabHeaders(true);
    }
    setRemainingPassTickets(data?.PrizeTicketModel?.SubscriptionTickets ?? 0);
    setPrizeDetails(data);
  };

  const onPopupCallback = () => {
    setShouldShowTabHeaders(false);
  };

  // Called from the confirmation screen's "Add more tickets" button. If the user has spent
  // all their pass tickets, drop the BOTB Pass tab and fall back to the Online entry tab.
  const onAddMoreTickets = (remaining: number) => {
    setRemainingPassTickets(remaining);
    setShouldShowTabHeaders(true);
    if (remaining <= 0) {
      setActiveTab("online");
    }
  };

  const hasPassTickets = remainingPassTickets > 0;

  const tabs: { key: TabKey; label: ReactNode; badge?: string }[] = [{ key: "online", label: "Online entry" }];
  if (hasPassTickets) {
    tabs.push({ key: "botbpass", label: "BOTB Pass entry" });
  }
  if (prizeDetails?.PostalEntryText) {
    tabs.push({
      key: "postal",
      label: (
        <>
          <span className="lg:hidden">Postal entry</span>
          <span className="hidden lg:inline">Free postal entry</span>
        </>
      ),
    });
  }
  if (subscriptionTab) {
    tabs.push({ key: "subscription", label: "Subscription", badge: "BEST VALUE" });
  }

  const getModalPopupTabs = () => {
    return (
      <div className="flex justify-center items-stretch">
        {tabs.map((tab, index) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`relative text-center cursor-pointer w-full px-2 py-[7px] lg:py-2.5 text-[13px] font-medium lg:text-[14px] font-jost border-b-2 border-solid whitespace-nowrap ${index === 0 ? "rounded-tl-[8px]" : ""} ${index === tabs.length - 1 ? "rounded-tr-[8px]" : ""} ${activeTab === tab.key ? "text-dark-1 bg-[#ff820029] border-b-[#ff8200]" : "text-[#696971] bg-[#f0f0f0] border-b-transparent"}`}>
            {tab.badge && (
              <span className="absolute right-3 -top-3 text-[10px] font-medium text-white text-center rounded bg-[#3DAE44] px-1 py-0.5">
                {tab.badge}
              </span>
            )}
            {tab.label}
          </button>
        ))}
      </div>
    );
  };

  const buyTicketProps = prizeDetails && {
    PriceWithoutCurrency: prizeDetails?.PrizeTicketModel?.PriceWithoutCurrency,
    defaultTicketCount: defaultTicketCounter || prizeDetails?.PrizeTicketModel?.DefaultTicketCount,
    baseTicketPrice: prizeDetails?.PrizeTicketModel?.FormattedOriginalPrice,
    baseTicketPriceWithoutCurrency: prizeDetails?.PrizeTicketModel?.FormattedOriginalPrice.split(
      prizeDetails?.PrizeTicketModel?.CurrencySymbol
    )[1],
    carImageSource: prizeDetails?.PrizeTicketModel?.ImageUrl,
    competitionId: prizeDetails?.PrizeTicketModel?.CompetitionId,
    currencySymbol: prizeDetails?.PrizeTicketModel?.CurrencySymbol,
    discountPercentage: prizeDetails?.PrizeTicketModel?.PercentOffValue,
    make: prizeDetails?.PrizeTicketModel?.Make,
    maxTicketsPerUser: prizeDetails?.PrizeTicketModel?.MaxTicketQuantity,
    model: prizeDetails?.PrizeTicketModel?.Model,
    overrideDiscount: Number(prizeDetails?.PrizeTicketModel?.OverrideDiscountValue),
    prizeId: prizeId,
    quickBuySelections: [
      prizeDetails?.PrizeTicketModel?.QuickBuyFirstButtonValue,
      prizeDetails?.PrizeTicketModel?.QuickBuySecondButtonValue,
      prizeDetails?.PrizeTicketModel?.QuickBuyThirdButtonValue,
      prizeDetails?.PrizeTicketModel?.MaxTicketQuantity,
    ],
    saleTicketPrice: prizeDetails?.PrizeTicketModel?.FormattedPrice,
    saleTicketPriceWithoutCurrency: prizeDetails?.PrizeTicketModel?.FormattedPriceWithoutCurrency,
    showInstantWinText: prizeDetails?.PrizeTicketModel?.ShowInstantWinText,
    multibuyLowerPercent: prizeDetails?.PrizeTicketModel?.MultiBuyLowerPercent,
    multibuyLowerQuantity: prizeDetails?.PrizeTicketModel?.MultiBuyLowerQuantity,
    multibuyUpperPercent: prizeDetails?.PrizeTicketModel?.MultiBuyUpperPercent,
    multibuyUpperQuantity: prizeDetails?.PrizeTicketModel?.MultiBuyUpperQuantity,
    subscriptionTickets: remainingPassTickets,
    onPopupCallback: onPopupCallback,
    onAddMoreTicketsCallback: onAddMoreTickets,
    SelectorMode: prizeDetails.PrizeTicketModel.TicketSelectorMode || "selectorButtons",
    competitionTypePrefix: prizeDetails.CompetitionTypePrefix,
    prizeBoostEndDate: prizeDetails?.PrizeTicketModel?.PrizeBoostEndDate,
    prizeBoostImageUrl: prizeDetails?.PrizeTicketModel?.PrizeBoostImageUrl,
    prizeBoostLowerQuantity: prizeDetails?.PrizeTicketModel?.PrizeBoostLowerQuantity,
    prizeBoostLowerValue: prizeDetails?.PrizeTicketModel?.PrizeBoostLowerValue,
    prizeBoostUpperQuantity: prizeDetails?.PrizeTicketModel?.PrizeBoostUpperQuantity,
    prizeBoostUpperValue: prizeDetails?.PrizeTicketModel?.PrizeBoostUpperValue,
    prizeBoostPromoText: prizeDetails?.PrizeTicketModel?.PrizeBoostPromoText,
    onAddTicketCallback: onAddTicketCallback,
    addToCartMethodId: addToCartMethodId,
    hasPassTickets: hasPassTickets,
  };

  return (
    prizeDetails && (
      <div className="flex flex-col gap-4 lg:gap-6">
        {shouldShowTabHeaders && tabs.length > 1 && getModalPopupTabs()}
        {activeTab === "online" && <BuyTicketPopup key="online" {...buyTicketProps} />}
        {activeTab === "botbpass" && <BuyTicketPopup key="botbpass" {...buyTicketProps} useSubscriptionTicketsMode />}
        {activeTab === "postal" && (
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(prizeDetails?.PostalEntryText),
            }}
            className={`overflow-auto max-lg:max-h-[60vh] max-lg:[@media(min-height:480px)]:max-h-[80vh] ${prizeDetails?.PrizeTicketModel?.ShowInstantWinText ? "max-lg:[@media(min-height:520px)]:h-[394px] lg:h-[432px]" : "max-lg:[@media(min-height:520px)]:h-[358px] lg:h-[396px]"}`}
          />
        )}
        {activeTab === "subscription" && (
          <SubscriptionTab
            competitionName={`${prizeDetails?.PrizeTicketModel?.Make} ${prizeDetails?.PrizeTicketModel?.Model}`}
            prizeDetails={prizeDetails}
          />
        )}
      </div>
    )
  );
};

export default PrizeTicketSelector;
